import { useState, useEffect, useRef, useContext } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { EDSHOP_INVITE, UPDATE_EDSHOP_MEMBER } from '../../graphql/mutations';
import { MEMBERS_LIST } from '../../graphql/queries';
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';

export const useStaff = () => {
    const { user, publisher, updatePublisherState } = useContext(PublisherAndUserContext);
    const [staffs, setStaffs] = useState([]);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        role: "staff",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isInvitingUser, setIsInvitingUser] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModelOpen,
        onClose: onDeleteModelClose,
      } = useDisclosure();
    const roleRef = useRef("")
    const toast = useToast();

    const [membersList] = useLazyQuery(MEMBERS_LIST, {
        onCompleted: data => {
            setLoading(false);
            const users = [...data?.edshopifyPublisherMembers?.additionalAdmins?.map(user => ({...user, role: "Co-Admin"})), ...data?.edshopifyPublisherMembers?.staff?.map(user => ({...user, role: "Staff"}))]
            setStaffs(users);
        },
        onError: (err) => {
            setLoading(false);
            console.log({err})
        }
    })

    const [inviteMember] = useMutation(EDSHOP_INVITE, {
        update: (cache, { data }, { variables }) => { 
            const exisitingMembers = cache.readQuery({query: MEMBERS_LIST})
            if(exisitingMembers) {
                cache.writeQuery({
                    query: MEMBERS_LIST, 
                    data: {
                        ...exisitingMembers, ...(variables.data.role === "staff" 
                        ? 
                        {staff: [data?.inviteEdShopifyMember, ...exisitingMembers.staff]} : 
                        {additionalAdmins: [data?.inviteEdShopifyMember, ...exisitingMembers.additionalAdmins]})}
                    })
            }
            setStaffs([{...data?.inviteEdShopifyMember, role: variables.data.role, isActive: true}, ...staffs]);
            updatePublisherState({
                ...publisher,
                ...(variables.data.role === "staff" 
                    ? 
                    {staff: [data?.inviteEdShopifyMember, ...publisher.staff]} : 
                    {additionalAdmins: [data?.inviteEdShopifyMember, ...publisher.additionalAdmins]})
            })
            setIsInvitingUser(false);
            toast({
                position: "top-right",
                description: `Invitation sent!`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
            onInviteModalClose();
        },
        onError: (err) => {
            setIsInvitingUser(false);
            toast({
                position: "top-right",
                description: `Failed to invite, Please try again.`,
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log({err})
        }
    });

    const [updateMember] = useMutation(UPDATE_EDSHOP_MEMBER, {
        onCompleted: data => {
            const staffsCopy = [...staffs];
            const index = staffsCopy.findIndex(staff => staff.id === data?.updateEdshopifyMember?.id)
            if(index > -1) {
                staffsCopy[index] = {
                    ...staffsCopy[index],
                    ...data?.updateEdshopifyMember?.id,
                    role: roleRef?.current,
                }
                setStaffs(staffsCopy);
            }
            setIsInvitingUser(false);
            onDeleteModelClose();
            onInviteModalClose();
            toast({
                position: "top-right",
                description: `${!data?.updateEdshopifyMember.isActive ? `Deactivated ${data?.updateEdshopifyMember.firstName} ${data?.updateEdshopifyMember.lastName}` : `Updated successfully!`}`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
            roleRef.current = ""
        },
        onError: (err) => {
            setIsInvitingUser(false);
            toast({
                position: "top-right",
                description: `Failed to update, Please try again.`,
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log({err})
        }
    });

    useEffect(() => {
        if(publisher?.id) {
            setLoading(true);
            membersList({
                variables: {
                    where: {
                        id: publisher?.id
                    }
                }
            })
        }
    }, [publisher?.id, membersList]);

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = "First name is required.";
        if (!formData.lastName.trim()) newErrors.lastName = "Last name is required.";
        if (!formData.email.trim()) newErrors.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const updateCallback = (data) => {
        setFormData({
            id: data?.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            role: data?.role?.toLowerCase(),
        });
        onOpen();
    }

    const deleteCallback = (data) => {
        setFormData({
            id: data?.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            role: data?.role?.toLowerCase(),
            isActive: data.isActive
        });
        onDeleteModelOpen();
    }

    const onInviteModalClose = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            role: "staff"
        });
        setErrors({});
        onClose();
    }

    const onDeletePopupClose = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            role: "staff"
        });
        setErrors({});
        onDeleteModelClose();
    }

    const handleConfirm = (e) => {
        e.preventDefault();
        if (!formData?.id && validate()) {
            setIsInvitingUser(true);
            inviteMember({ 
                variables: { 
                    where: {
                        id: publisher?.id
                    },
                    data: {
                        ...formData
                    }
                } 
            });
            return true;
        }
        if(formData?.id && validate()) {
            setIsInvitingUser(true);
            roleRef.current = formData?.role
            updateMember({
                variables: {
                    where: {
                        userId: formData?.id,
                        edShopId: publisher?.id
                    },
                    data: {
                        firstName: formData?.firstName,
                        lastName: formData?.lastName,
                        isActive: true,
                        role: formData?.role
                    }
                }
            })
        }
    };

    const deActivateMember = () => {
        setIsInvitingUser(true);
        roleRef.current = formData?.role
        updateMember({
            variables: {
                where: {
                    userId: formData?.id,
                    edShopId: publisher?.id
                },
                data: {
                    firstName: formData?.firstName,
                    lastName: formData?.lastName,
                    isActive: !formData?.isActive,
                }
            }
        })
    }

    return {
        staffs,
        formData,
        errors,
        loading,
        isInvitingUser,
        isOpen,
        onOpen,
        isDeleteModalOpen,
        onDeletePopupClose,
        handleChange,
        updateCallback,
        deleteCallback,
        onInviteModalClose,
        deActivateMember,
        handleConfirm,
        user,
        publisher
    }
}
